// Reseller Service - GraphQL operations for Reseller role
import { graphqlClient } from '../graphqlClient';

// ==================== Profile ====================

export const getProfile = async () => {
  const query = `
    query GetResellerProfile {
      resellerProfile {
        id
        email
        name
        status
        code
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        contactPhone
        supportEmail
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.resellerProfile;
};

export const updateProfile = async (input) => {
  const mutation = `
    mutation ResellerUpdateProfile($input: ResellerUpdateProfileInput!) {
      resellerUpdateProfile(input: $input) {
        id
        email
        name
        status
        code
        contactPhone
        supportEmail
        apiKey
        secretKey
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.resellerUpdateProfile;
};

// ==================== Merchant Operations ====================

export const getMyMerchants = async () => {
  const query = `
    query GetResellerMerchants {
      resellerMerchants {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        createdByResellerId
        currency
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.resellerMerchants;
};

export const getMerchantsByResellerId = async (resellerId) => {
  const query = `
    query GetResellerMerchantsByResellerId($resellerId: String!) {
      resellerMerchantsByResellerId(resellerId: $resellerId)
    }
  `;
  const result = await graphqlClient.query(query, { resellerId });
  return result.resellerMerchantsByResellerId;
};

export const getMerchantById = async (id) => {
  const query = `
    query GetResellerMerchantById($id: String!) {
      resellerMerchantById(id: $id) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        fee_type
        fee_rate
        currency
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query, { id });
  return result.resellerMerchantById;
};

export const createMerchant = async (input) => {
  const mutation = `
    mutation ResellerCreateMerchant($input: ResellerCreateMerchantInput!) {
      resellerCreateMerchant(input: $input) {
        id
        code
        email
        name
        status
        currency
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.resellerCreateMerchant;
};

export const updateMerchant = async (id, input) => {
  const mutation = `
    mutation ResellerUpdateMerchant($id: String!, $input: ResellerUpdateMerchantInput!) {
      resellerUpdateMerchant(id: $id, input: $input) {
        id
        code
        email
        name
        status
        currency
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.resellerUpdateMerchant;
};

export const deleteMerchant = async (id) => {
  const mutation = `
    mutation ResellerDeleteMerchant($id: String!) {
      resellerDeleteMerchant(id: $id)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id });
  return result.resellerDeleteMerchant;
};

// ==================== Dashboard Stats ====================

export const getDashboardStats = async () => {
  const query = `
    query GetResellerDashboardStats {
      resellerDashboardStats {
        totalMerchants
        activeMerchants
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.resellerDashboardStats;
};

export const getPanelDashboardStats = async (startDate, endDate, merchantIds) => {
  const query = `
    query ResellerPanelDashboardStats($startDate: String, $endDate: String, $merchantIds: [String!]) {
      resellerPanelDashboardStats(startDate: $startDate, endDate: $endDate, merchantIds: $merchantIds) {
        totalVolume
        totalDeposits
        totalWithdrawals
        successRate
        activeMerchants
      }
    }
  `;

  const result = await graphqlClient.query(query, {
    startDate: startDate ?? null,
    endDate: endDate ?? null,
    merchantIds: merchantIds ?? null,
  });

  return (
    result?.resellerPanelDashboardStats ?? {
      totalVolume: "0.00",
      totalDeposits: "0.00",
      totalWithdrawals: "0.00",
      successRate: "0.00",
      activeMerchants: 0,
    }
  );
};

// ==================== Webhook Logs ====================

export const getWebhookLogs = async (page = 1, limit = 50) => {
  const query = `
    query GetResellerWebhookLogs($page: Int, $limit: Int) {
      resellerWebhookLogs(page: $page, limit: $limit) {
        id
        qrCodeId
        paymentId
        merchantId
        merchantName
        event
        url
        httpStatus
        responseBody
        errorMessage
        status
        direction
        requestPayload
        createdAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { page, limit });
  return result.resellerWebhookLogs || [];
};

